import React from "react"
import PropTypes from "prop-types"
import { Trans } from "react-i18next"
import {
  Alert,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Link,
} from "@mui/material"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import i18n from "i18n"
import usePdfDownload from "../hooks/usePdfDownload"

const Header = ({
  title = "",
  showBack = false,
  onBack,
  onSave,
  onIssueInvoice,
  invoiceData,
  correctsInvoiceData = null,
  correctedByInvoiceData = null,
  corrections = [],
  issued = false,
  isSaving = false,
  isIssuing = false,
  useInvopop = false,
}) => {
  const isOldInvoice = invoiceData && invoiceData.oldInvoiceId && !issued
  const invoiceType = invoiceData?.invoiceType
  const isCorrective = invoiceType === "corrective"
  const isCreditNote = invoiceType === "credit-note"
  const isDebitNote = invoiceType === "debit-note"
  const isCorrected = Boolean(invoiceData && invoiceData.correctedBy)
  const isCancelled = Boolean(invoiceData && invoiceData.cancelledAt)

  // El backend guarda la anulación dentro de `corrections`, apuntando a la propia factura:
  // incluirla haría anunciar una rectificativa inexistente enlazada a sí misma.
  const linkedCorrections = corrections.filter(
    c => String(c.type) !== "cancellation",
  )

  const invopopStatus = invoiceData?.invopopData?.invopopStatus
  // El backend solo escribe siloId cuando envía a Verifactu, así que su ausencia distingue
  // "nunca se intentó declarar" (organización sin Invopop, o factura anterior a la
  // integración) de "se intentó y falló". Sin esa distinción, `not_sent` acusa de un fallo
  // inexistente a facturas que nunca debieron declararse.
  const hasInvopopAttempt = Boolean(invoiceData?.invopopData?.siloId)
  // Reintentar solo tiene sentido si la organización puede enviar: sin `useInvopop`,
  // `send_to_invopop` responde ValueError, así que ofrecerlo es ofrecer un fallo seguro.
  const canReissue =
    issued &&
    useInvopop &&
    ["not_sent", "error", "failing", "rejected"].includes(invopopStatus)
  const isIssuedNotSent =
    issued && invopopStatus === "not_sent" && hasInvopopAttempt

  const { isDownloading, downloadPdf } = usePdfDownload(invoiceData)

  const correctsInvoiceNumber = correctsInvoiceData?.invoiceNumber || ""
  const correctsInvoiceHref = correctsInvoiceData?.invoiceId
    ? `/invoice/${correctsInvoiceData.invoiceId}`
    : null

  const correctedByIsDraft = Boolean(
    correctedByInvoiceData && !correctedByInvoiceData.issued,
  )
  const correctedByInvoiceNumber = correctedByInvoiceData?.invoiceNumber || ""
  const correctedByInvoiceHref = correctedByInvoiceData?.invoiceId
    ? `/invoice/${correctedByInvoiceData.invoiceId}`
    : null

  const getSaveLabel = () => {
    if (isSaving) return <CircularProgress size={22} color="inherit" />
    if (issued) return i18n.t("invoices.save")
    if (isOldInvoice) return i18n.t("invoices.draft")
    return i18n.t("invoices.saveDraft")
  }

  const getIssueLabel = () => {
    if (isIssuing) return <CircularProgress size={22} color="inherit" />
    if (canReissue) return i18n.t("invoices.reissue")
    if (issued || isOldInvoice) return i18n.t("invoices.issued")
    return i18n.t("invoices.issue")
  }

  return (
    <>
      {isCancelled && (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {i18n.t("invoices.invoiceCancelledNotice")}
        </Alert>
      )}
      {["pending", "processing"].includes(invopopStatus) && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {i18n.t("invoices.invopopPendingNotice")}
        </Alert>
      )}
      {isIssuedNotSent && !isCancelled && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mb: 2 }}
          data-testid="invoice-not-sent-notice"
        >
          {i18n.t("invoices.notSentNotice")}
        </Alert>
      )}
      {isCorrective && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {correctsInvoiceNumber && correctsInvoiceHref ? (
            <Trans
              i18nKey={
                issued
                  ? "invoices.correctiveInvoiceIssuedNoticeWithNumber"
                  : "invoices.correctiveInvoiceNoticeWithNumber"
              }
              values={{ invoiceNumber: correctsInvoiceNumber }}
              components={[
                null,
                <Link
                  key="corrects-invoice-link"
                  href={correctsInvoiceHref}
                  underline="always"
                  sx={{
                    fontWeight: 700,
                    cursor: "pointer",
                    color: "primary.main",
                  }}
                />,
              ]}
            />
          ) : (
            i18n.t(
              issued
                ? "invoices.correctiveInvoiceIssuedNotice"
                : "invoices.correctiveInvoiceNotice",
            )
          )}
        </Alert>
      )}
      {isCreditNote && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {correctsInvoiceNumber && correctsInvoiceHref ? (
            <Trans
              i18nKey={
                issued
                  ? "invoices.creditNoteIssuedNoticeWithNumber"
                  : "invoices.creditNoteNoticeWithNumber"
              }
              values={{ invoiceNumber: correctsInvoiceNumber }}
              components={[
                null,
                <Link
                  key="credit-note-link"
                  href={correctsInvoiceHref}
                  underline="always"
                  sx={{
                    fontWeight: 700,
                    cursor: "pointer",
                    color: "primary.main",
                  }}
                />,
              ]}
            />
          ) : (
            i18n.t("invoices.creditNoteNotice")
          )}
        </Alert>
      )}
      {isDebitNote && (
        <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
          {correctsInvoiceNumber && correctsInvoiceHref ? (
            <Trans
              i18nKey={
                issued
                  ? "invoices.debitNoteIssuedNoticeWithNumber"
                  : "invoices.debitNoteNoticeWithNumber"
              }
              values={{ invoiceNumber: correctsInvoiceNumber }}
              components={[
                null,
                <Link
                  key="debit-note-link"
                  href={correctsInvoiceHref}
                  underline="always"
                  sx={{
                    fontWeight: 700,
                    cursor: "pointer",
                    color: "primary.main",
                  }}
                />,
              ]}
            />
          ) : (
            i18n.t("invoices.debitNoteNotice")
          )}
        </Alert>
      )}
      {isCorrected && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {correctedByIsDraft && correctedByInvoiceHref ? (
            <Trans
              i18nKey="invoices.correctedByDraftNotice"
              components={[
                null,
                <Link
                  key="corrected-by-link"
                  href={correctedByInvoiceHref}
                  underline="always"
                  sx={{
                    fontWeight: 700,
                    cursor: "pointer",
                    color: "primary.main",
                  }}
                />,
              ]}
            />
          ) : correctedByInvoiceNumber && correctedByInvoiceHref ? (
            <Trans
              i18nKey="invoices.correctedByNoticeWithNumber"
              values={{ invoiceNumber: correctedByInvoiceNumber }}
              components={[
                null,
                <Link
                  key="corrected-by-link"
                  href={correctedByInvoiceHref}
                  underline="always"
                  sx={{
                    fontWeight: 700,
                    cursor: "pointer",
                    color: "primary.main",
                  }}
                />,
              ]}
            />
          ) : (
            i18n.t("invoices.correctedByNotice")
          )}
        </Alert>
      )}
      {linkedCorrections.length > 0 && !isCorrected && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {(() => {
            const issued = linkedCorrections.filter(c => c.invoiceNumber)
            const drafts = linkedCorrections.filter(c => !c.invoiceNumber)
            const parts = [
              ...issued.map(c => (
                <Link
                  key={c.invoiceId}
                  href={`/invoice/${c.invoiceId}`}
                  underline="always"
                  sx={{
                    fontWeight: 700,
                    cursor: "pointer",
                    color: "primary.main",
                  }}
                >
                  {c.invoiceNumber}
                </Link>
              )),
              ...(drafts.length > 0
                ? drafts.map(c => (
                    <Link
                      key={c.invoiceId}
                      href={`/invoice/${c.invoiceId}`}
                      underline="always"
                      sx={{
                        fontWeight: 700,
                        cursor: "pointer",
                        color: "primary.main",
                      }}
                    >
                      {i18n.t("invoices.draft")}
                    </Link>
                  ))
                : []),
            ]
            return (
              <span>
                {linkedCorrections.length > 1
                  ? i18n.t("invoices.adjustedByNoticeMultiple")
                  : i18n.t("invoices.adjustedByNotice")}{" "}
                {parts.reduce(
                  (acc, el, i) => [
                    ...acc,
                    i > 0 ? <span key={`sep-${i}`}>, </span> : null,
                    el,
                  ],
                  [],
                )}
              </span>
            )
          })()}
        </Alert>
      )}
      {isOldInvoice && (
        <Alert
          severity="warning"
          variant="filled"
          sx={{ bgcolor: "#ff931E", mb: 4, borderColor: "#ff931E" }}
          action={
            <Button
              component="a"
              href="https://help.planhopper.com/es/articles/13297499-como-revisar-y-completar-facturas-migradas-en-el-nuevo-modulo-de-facturacion"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                fontWeight: 500,
                color: "#fff",
                ml: 1,
                "&:hover, &:focus": {
                  color: "#fff",
                  outline: "2px solid #fff",
                  outlineOffset: "2px",
                },
              }}
            >
              {i18n.t("invoices.viewHelpArticle")}
            </Button>
          }
        >
          {i18n.t("invoices.invoiceNeedsReview")}
        </Alert>
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%", marginBottom: "20px" }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          {showBack && (
            <IconButton onClick={onBack} size="small">
              <ChevronLeftIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="h1">
            {title || i18n.t("invoices.invoice")}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1}>
          {invoiceData?.invoiceId && !isOldInvoice && (
            <Button
              variant="outlined"
              color="primary"
              onClick={downloadPdf}
              disabled={isDownloading}
              data-testid="download-invoice-pdf-button"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <FileDownloadIcon />
                )
              }
            >
              {i18n.t("invoices.downloadPdf")}
            </Button>
          )}
          {isOldInvoice && (
            <Box sx={{ mt: 1 }}>{i18n.t("invoices.saveAs")}</Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={isSaving}
            sx={{ minWidth: "180px" }}
          >
            {getSaveLabel()}
          </Button>
          <Button
            variant="contained"
            color="branded"
            onClick={onIssueInvoice}
            disabled={(!canReissue && issued) || isIssuing}
            sx={{ minWidth: "90px" }}
          >
            {getIssueLabel()}
          </Button>
        </Stack>
      </Stack>
    </>
  )
}

export default Header

Header.propTypes = {
  title: PropTypes.string,
  showBack: PropTypes.bool,
  onSave: PropTypes.func,
  onIssueInvoice: PropTypes.func,
  onBack: PropTypes.func,
  issued: PropTypes.bool,
  invoiceData: PropTypes.object,
  correctsInvoiceData: PropTypes.object,
  correctedByInvoiceData: PropTypes.object,
  corrections: PropTypes.arrayOf(PropTypes.object),
  isSaving: PropTypes.bool,
  isIssuing: PropTypes.bool,
  useInvopop: PropTypes.bool,
}
